/**
 * The football knowledge base (PRD §22, §23, §24).
 *
 * These are curated, deterministic explanations, never AI-generated
 * (Implementation Guide §22). Every explanation in the product comes from
 * here: the live game, the Learn tab, quizzes and the post-game recap all
 * reference the same record, so a concept is never explained two ways.
 *
 * Record shape:
 *   conceptId            stable slug, also the Firestore document id
 *   title                display name
 *   emoji                one glyph, used for quick visual recognition
 *   category             grouping label for the Learn tab
 *   difficulty           1 basics · 2 mechanics · 3 strategy · 4 advanced
 *   shortExplanation     one sentence, fits on a phone in-game
 *   beginnerExplanation  two or three sentences, plain English
 *   whyItMatters         why a viewer should care
 *   examples             concrete things they might see or hear
 *   relatedConcepts      other conceptIds worth reading next
 */

export const DIFFICULTY = Object.freeze({
  BASICS: 1,
  MECHANICS: 2,
  STRATEGY: 3,
  ADVANCED: 4,
});

export const DIFFICULTY_LABELS = Object.freeze({
  1: "Basics",
  2: "How the game works",
  3: "Strategy",
  4: "Advanced",
});

export const CONCEPTS = Object.freeze([
  /* ------------------------------------------------ the three units */
  {
    conceptId: "units",
    title: "Three Teams in One",
    emoji: "🔀",
    category: "The three units",
    difficulty: DIFFICULTY.BASICS,
    shortExplanation:
      "A club isn't one team of 11. It's three squads that swap on and off.",
    beginnerExplanation:
      "Only 11 players from each club are on the field at once, but a squad has over 50. They're split into three groups that almost never overlap: the offence plays when their club has the ball, the defence plays when it doesn't, and special teams come on for kicks. When possession changes, roughly 22 players walk off and 22 different ones walk on.",
    whyItMatters:
      "It explains something that confuses almost every new viewer: the players you were just watching disappear, and a completely different set jogs on. Nothing has gone wrong. That's the sport working normally.",
    examples: [
      "Buffalo punts, and every Buffalo player you were watching leaves the field.",
      "When a commentator says \"their defence has been on the field too long\", they mean a different group of people from the ones who just scored.",
    ],
    relatedConcepts: ["offence", "defence", "special-teams"],
  },
  {
    conceptId: "offence",
    title: "The Offence",
    emoji: "⚔️",
    category: "The three units",
    difficulty: DIFFICULTY.BASICS,
    shortExplanation: "The 11 players whose club has the ball. Their job: score.",
    beginnerExplanation:
      "The offence is on the field whenever their club has the ball. It's built around the quarterback, with linemen protecting him, running backs to carry the ball, and receivers to catch it. They get four attempts to move 10 yards, and they keep the ball for as long as they keep managing it.",
    whyItMatters:
      "\"Offence\" is shorthand for \"the team with the ball right now\". Once you can tell which side is on offence, the down, the distance and the whole commentary start making sense.",
    examples: [
      "\"Buffalo's offence is on the field\" means Buffalo has the ball.",
      "A team with a good offence scores a lot; it says nothing about how well they defend.",
    ],
    relatedConcepts: ["units", "defence", "possession"],
  },
  {
    conceptId: "defence",
    title: "The Defence",
    emoji: "🛑",
    category: "The three units",
    difficulty: DIFFICULTY.BASICS,
    shortExplanation:
      "The 11 players trying to stop the other club, and take the ball back.",
    beginnerExplanation:
      "The defence comes on when the other club has the ball. Linemen try to reach the quarterback, linebackers cover the middle, and defensive backs cover the receivers. They're trying to stop the offence gaining 10 yards and, if they can, take the ball off them with an interception or a fumble.",
    whyItMatters:
      "Defence is why a drive ends. Every punt, every turnover and every stop on third down is the defence doing its job, and it's half of what decides a game.",
    examples: [
      "\"Third down, and the defence needs a stop\": stop them here and their club gets the ball back.",
      "A defence that forces turnovers hands its own offence the ball in good positions.",
    ],
    relatedConcepts: ["units", "offence", "interception"],
  },
  {
    conceptId: "special-teams",
    title: "Special Teams",
    emoji: "🦵",
    category: "The three units",
    difficulty: DIFFICULTY.BASICS,
    shortExplanation:
      "The third squad. They come on whenever the ball is going to be kicked.",
    beginnerExplanation:
      "Any time the ball is kicked (a kickoff, a punt, a field goal, an extra point), a third group of players comes on. Most of them play nowhere else. It's a small part of the game by number of plays, and a huge part by consequence: a blocked kick or a long return can decide a match.",
    whyItMatters:
      "It's the answer to \"who are these people?\" every time the kicking unit runs on, and it's why a missed kick feels so much bigger than one play should.",
    examples: [
      "Fourth down, and the punt team jogs on: that's special teams.",
      "A returner catching a kickoff and sprinting upfield is a special teams play.",
    ],
    relatedConcepts: ["units", "punt", "field-goal"],
  },

  /* ------------------------------------------------ scoring */
  {
    conceptId: "touchdown",
    title: "Touchdown",
    emoji: "🏈",
    category: "Scoring",
    difficulty: DIFFICULTY.BASICS,
    shortExplanation: "The big one: 6 points for getting the ball into the end zone.",
    beginnerExplanation:
      "A touchdown is worth 6 points. It happens when a player carries the ball across the other team's goal line, or catches it while standing in the end zone. This is what every drive is trying to achieve.",
    whyItMatters:
      "It's the most valuable single play in football, and it's why everyone in the room suddenly stands up.",
    examples: [
      "A running back crosses the goal line with the ball.",
      "A receiver catches a pass with both feet inside the end zone.",
    ],
    relatedConcepts: ["extra-point", "two-point-conversion", "red-zone"],
  },
  {
    conceptId: "field-goal",
    title: "Field Goal",
    emoji: "🥅",
    category: "Scoring",
    difficulty: DIFFICULTY.BASICS,
    shortExplanation: "3 points for kicking the ball through the tall yellow posts.",
    beginnerExplanation:
      "When a team gets close enough but can't reach the end zone, they can kick the ball through the yellow uprights for 3 points. If the kick misses, the other team takes over the ball.",
    whyItMatters:
      "It's the consolation prize for a drive that stalls, and in a close game three points decide it.",
    examples: [
      "It's 4th down at the opponent's 20-yard line, so the kicker comes on.",
      "A last-second kick to win the game.",
    ],
    relatedConcepts: ["field-goal-range", "fourth-down", "touchdown"],
  },
  {
    conceptId: "extra-point",
    title: "Extra Point",
    emoji: "☝️",
    category: "Scoring",
    difficulty: DIFFICULTY.BASICS,
    shortExplanation: "A short kick worth 1 point, taken right after a touchdown.",
    beginnerExplanation:
      "After scoring a touchdown, the team gets one bonus attempt. The usual choice is a short kick through the uprights for 1 point, snapped from the 15-yard line, about a 33-yard kick. It usually goes in, which is why a touchdown is normally worth 7 in total.",
    whyItMatters:
      "It's the reason scores are so often 7, 14, 21 and 28 rather than multiples of 6.",
    examples: ["Buffalo scores a touchdown, then kicks the extra point: 7 points."],
    relatedConcepts: ["touchdown", "two-point-conversion"],
  },
  {
    conceptId: "two-point-conversion",
    title: "Two-Point Conversion",
    emoji: "✌️",
    category: "Scoring",
    difficulty: DIFFICULTY.MECHANICS,
    shortExplanation:
      "Skipping the easy kick to run one play for 2 points instead.",
    beginnerExplanation:
      "Instead of kicking the extra point, a team can run a single play from the 2-yard line. Get the ball into the end zone and it's worth 2 points; fail and they get nothing. It's riskier than the kick, so teams save it for when the scoreboard demands it.",
    whyItMatters:
      "When a team goes for two, the coach has done maths: they need a specific final margin.",
    examples: ["Down by 2 late in the game, a team scores and goes for two to tie."],
    relatedConcepts: ["extra-point", "touchdown", "clock"],
  },

  /* ------------------------------------------------ downs and distance */
  {
    conceptId: "down",
    title: "Down",
    emoji: "1️⃣",
    category: "Downs & distance",
    difficulty: DIFFICULTY.BASICS,
    shortExplanation: "One attempt. The offense gets four of them at a time.",
    beginnerExplanation:
      "A down is a single attempt to move the ball. The team with the ball gets four downs to gain 10 yards. If they make it, they get four fresh downs. If they don't, the other team takes over.",
    whyItMatters:
      "Almost everything you hear ('3rd and 7', 'go for it on fourth') is about downs. Understand downs and the whole game opens up.",
    examples: ["\"2nd and 6\" means it's the second attempt with 6 yards still to go."],
    relatedConcepts: ["first-down", "yards", "fourth-down"],
  },
  {
    conceptId: "yards",
    title: "Yards",
    emoji: "📏",
    category: "Downs & distance",
    difficulty: DIFFICULTY.BASICS,
    shortExplanation: "The unit of progress. 100 yards between the two end zones.",
    beginnerExplanation:
      "The field is 100 yards long between the goal lines, with lines marked every 5 yards. Everything in football is measured in yards gained or lost, and the numbers painted on the field count up to 50 in the middle and back down again.",
    whyItMatters:
      "Yards are the score behind the score: teams that move the ball further usually win.",
    examples: ["\"A 12-yard gain\" moved the ball 12 yards closer to the end zone."],
    relatedConcepts: ["down", "first-down", "field-goal-range"],
  },
  {
    conceptId: "first-down",
    title: "First Down",
    emoji: "🔟",
    category: "Downs & distance",
    difficulty: DIFFICULTY.BASICS,
    shortExplanation: "Gain 10 yards and you get a brand-new set of four attempts.",
    beginnerExplanation:
      "Move the ball 10 yards from where the current set of downs started and you earn a first down. The count resets: it's 1st and 10 again, with four fresh attempts. Keep earning first downs and you can keep the ball all day.",
    whyItMatters:
      "It's how a team stays on the field. No first downs means giving the ball back quickly.",
    examples: [
      "\"They picked up the first down\": they keep the ball and start over at 1st and 10.",
      "You'll see an orange marker on the sideline showing the line they need to reach.",
    ],
    relatedConcepts: ["down", "third-down", "yards"],
  },
  {
    conceptId: "second-down",
    title: "Second Down",
    emoji: "2️⃣",
    category: "Downs & distance",
    difficulty: DIFFICULTY.MECHANICS,
    shortExplanation: "Attempt two of four. Still relaxed, with plenty of options left.",
    beginnerExplanation:
      "Second down is the middle of a set. Teams still have two more attempts after this one, so they can try almost anything: a run, a short pass, or a deep shot downfield.",
    whyItMatters:
      "What happens on second down decides whether third down is easy or desperate.",
    examples: ["\"2nd and 3\" is comfortable. \"2nd and 15\" means the last play went badly."],
    relatedConcepts: ["down", "first-down", "third-down"],
  },
  {
    conceptId: "third-down",
    title: "Third Down",
    emoji: "3️⃣",
    category: "Downs & distance",
    difficulty: DIFFICULTY.MECHANICS,
    shortExplanation: "The pressure down. Make it or you'll probably punt.",
    beginnerExplanation:
      "Third down is usually the last realistic chance to keep the ball, because most teams punt on fourth. That's why the crowd gets loud on third down: if the defence stops them here, the offence has to give the ball back.",
    whyItMatters:
      "It's the single most-watched down in football. Converting third downs is how drives stay alive.",
    examples: ["\"3rd and 7\" means they need 7 yards on this play or they're likely punting."],
    relatedConcepts: ["fourth-down", "first-down", "punt"],
  },
  {
    conceptId: "fourth-down",
    title: "Fourth Down",
    emoji: "4️⃣",
    category: "Downs & distance",
    difficulty: DIFFICULTY.MECHANICS,
    shortExplanation: "Last attempt. Punt, kick a field goal, or gamble.",
    beginnerExplanation:
      "On fourth down the team has three choices: punt the ball away, kick a field goal if they're close enough, or 'go for it' and run a normal play. Going for it and failing hands the other team the ball right there, which is why it's a big decision.",
    whyItMatters:
      "This is the down where coaches take real risks. It's the most argued-about moment in any game.",
    examples: [
      "\"They're going for it on 4th and 1\": one yard to keep the drive alive.",
      "\"4th and 12 from their own 30\": almost always a punt.",
    ],
    relatedConcepts: ["punt", "field-goal", "third-down"],
  },

  /* ------------------------------------------------ possession changes */
  {
    conceptId: "possession",
    title: "Possession",
    emoji: "🙌",
    category: "Possession",
    difficulty: DIFFICULTY.BASICS,
    shortExplanation: "Simply: which team currently has the ball.",
    beginnerExplanation:
      "Only one team has the ball at a time. That team is on offence and is trying to score; the other team is on defence and is trying to stop them. Possession changes on a punt, a score, a turnover, or a failed fourth down.",
    whyItMatters:
      "Everything else (downs, distance, strategy) only makes sense once you know who has the ball.",
    examples: ["\"Buffalo ball\" means Buffalo is on offence right now."],
    relatedConcepts: ["offence", "defence", "units"],
  },
  {
    conceptId: "punt",
    title: "Punt",
    emoji: "🦶",
    category: "Possession",
    difficulty: DIFFICULTY.BASICS,
    shortExplanation: "Giving up the ball on purpose, but from as far away as possible.",
    beginnerExplanation:
      "On fourth down, rather than risk handing the other team the ball in good position, a team kicks it far downfield. They lose possession either way; punting just makes the other team start much further from the end zone.",
    whyItMatters:
      "It looks like surrender, but it's field-position strategy. A good punt can be worth 40 yards of defence.",
    examples: [
      "\"4th and 9 from their own 25\": the punt team runs on.",
      "A punt that stops inside the 5-yard line is a great play, even though they gave the ball away.",
    ],
    relatedConcepts: ["fourth-down", "possession", "kickoff"],
  },
  {
    conceptId: "kickoff",
    title: "Kickoff",
    emoji: "🦵",
    category: "Possession",
    difficulty: DIFFICULTY.BASICS,
    shortExplanation: "The kick that starts each half and follows every score.",
    beginnerExplanation:
      "A kickoff sends the ball down the field to the other team, who can try to run it back. It's how each half begins and how play restarts after a touchdown or field goal.",
    whyItMatters:
      "It decides where the next drive starts, which shapes everything that comes after it.",
    examples: ["After a touchdown, the scoring team kicks off to the other team."],
    relatedConcepts: ["possession", "touchdown", "field-goal"],
  },
  {
    conceptId: "interception",
    title: "Interception",
    emoji: "😱",
    category: "Possession",
    difficulty: DIFFICULTY.BASICS,
    shortExplanation: "The defence catches a pass meant for the offence. Ball changes sides instantly.",
    beginnerExplanation:
      "The quarterback throws the ball, but a defensive player catches it instead of the intended receiver. Possession switches immediately, and the defender can run with it. It's one of the worst things that can happen to an offence.",
    whyItMatters:
      "A turnover can swing a game in one play, and it's the loudest three seconds in the stadium.",
    examples: ["A cornerback jumps in front of the receiver and catches the ball."],
    relatedConcepts: ["fumble", "possession", "quarterback"],
  },
  {
    conceptId: "fumble",
    title: "Fumble",
    emoji: "🤲",
    category: "Possession",
    difficulty: DIFFICULTY.BASICS,
    shortExplanation: "A player drops the ball while carrying it. Whoever grabs it, gets it.",
    beginnerExplanation:
      "A fumble is when a player loses the ball before being down. The ball is live: either team can recover it. If the defence gets it, possession changes; if the offence recovers it, they keep the ball.",
    whyItMatters:
      "It's chaos: the one moment where the ball genuinely belongs to nobody.",
    examples: ["A running back is hit, the ball pops loose, and everyone dives on it."],
    relatedConcepts: ["interception", "possession", "running-back"],
  },
  {
    conceptId: "incomplete-pass",
    title: "Incomplete Pass",
    emoji: "🚫",
    category: "Possession",
    difficulty: DIFFICULTY.BASICS,
    shortExplanation: "The pass hits the ground. No gain, and the clock stops.",
    beginnerExplanation:
      "If a thrown ball isn't caught, the play is over immediately. Nobody gains or loses yards, the next down begins from the same spot, and, importantly, the game clock stops.",
    whyItMatters:
      "Late in a game, throwing incomplete passes on purpose is how a losing team saves time.",
    examples: ["The receiver reaches for the ball and it bounces off his fingertips."],
    relatedConcepts: ["clock", "wide-receiver", "down"],
  },
  {
    conceptId: "sack",
    title: "Sack",
    emoji: "💥",
    category: "Possession",
    difficulty: DIFFICULTY.MECHANICS,
    shortExplanation: "The quarterback is tackled before he can throw. The offence loses yards.",
    beginnerExplanation:
      "A sack is when defenders get to the quarterback behind the line of scrimmage while he's still trying to pass. The offence loses whatever yards he was pushed back, which makes the next down much harder.",
    whyItMatters:
      "A sack doesn't just waste a down: it moves the target further away. '2nd and 4' can become '3rd and 12' in one play.",
    examples: ["A defensive lineman gets past the blocker and brings the quarterback down."],
    relatedConcepts: ["quarterback", "blitz", "defensive-line"],
  },

  /* ------------------------------------------------ rules and field */
  {
    conceptId: "penalty",
    title: "Penalty",
    emoji: "🚩",
    category: "Rules & field",
    difficulty: DIFFICULTY.MECHANICS,
    shortExplanation: "Somebody broke a rule. A yellow flag flies and yards change hands.",
    beginnerExplanation:
      "When an official sees a rule broken, they throw a yellow flag. The punishment is usually yards: the offending team moves backwards, or the other team moves forwards. Some penalties also award an automatic first down or wipe out the play entirely.",
    whyItMatters:
      "Penalties can erase a touchdown or hand a team a first down for free. They change games.",
    examples: [
      "\"False start\": an offensive player moved before the snap. 5 yards back.",
      "\"Holding\": a blocker grabbed a defender illegally. Usually 10 yards.",
    ],
    relatedConcepts: ["down", "first-down", "yards"],
  },
  {
    conceptId: "red-zone",
    title: "Red Zone",
    emoji: "🎯",
    category: "Rules & field",
    difficulty: DIFFICULTY.MECHANICS,
    shortExplanation: "Inside the opponent's 20-yard line: close enough to smell the end zone.",
    beginnerExplanation:
      "The red zone is the last 20 yards before the end zone. Scoring gets harder here because the defence has less field to protect, so everyone is packed tightly together. Teams that keep settling for field goals in the red zone tend to lose.",
    whyItMatters:
      "When a broadcaster says 'red zone', it means points are very likely in the next few plays.",
    examples: ["Buffalo has 1st and 10 at the Carolina 18, so they're in the red zone."],
    relatedConcepts: ["touchdown", "field-goal", "yards"],
  },
  {
    conceptId: "field-goal-range",
    title: "Field Goal Range",
    emoji: "📍",
    category: "Rules & field",
    difficulty: DIFFICULTY.MECHANICS,
    shortExplanation: "Close enough that the kicker can realistically make it.",
    beginnerExplanation:
      "Kicks are measured from about 17 yards behind the ball's position, so reaching the opponent's 35-yard line means roughly a 52-yard attempt, around the edge of what most kickers make reliably. Getting into field goal range is often a drive's minimum goal.",
    whyItMatters:
      "It explains why a team suddenly plays it safe: they've reached kicking distance and don't want to lose it.",
    examples: ["\"They just need 10 more yards to get into field goal range.\""],
    relatedConcepts: ["field-goal", "clock", "yards"],
  },
  {
    conceptId: "clock",
    title: "The Clock",
    emoji: "⏱️",
    category: "Rules & field",
    difficulty: DIFFICULTY.MECHANICS,
    shortExplanation: "Four 15-minute quarters, but it stops far more often than you'd think.",
    beginnerExplanation:
      "A game is four quarters of 15 minutes. The clock stops on incomplete passes, when a player goes out of bounds, after a score, and on timeouts. That's why the last two minutes can take twenty.",
    whyItMatters:
      "Late in a close game, managing the clock matters as much as gaining yards. Teams will deliberately stay in bounds to run it down, or throw the ball away to stop it.",
    examples: [
      "A losing team throws short passes near the sideline so the receiver can step out and stop the clock.",
      "A winning team runs the ball to keep the clock moving.",
    ],
    relatedConcepts: ["incomplete-pass", "two-point-conversion", "fourth-down"],
  },

  /* ------------------------------------------------ offence positions */
  {
    conceptId: "quarterback",
    title: "Quarterback (QB)",
    emoji: "🎯",
    category: "Who's who",
    difficulty: DIFFICULTY.BASICS,
    shortExplanation: "The one who takes the ball first every play and decides what happens next.",
    beginnerExplanation:
      "The quarterback receives the ball at the start of nearly every offensive play, then hands it off, throws it, or runs with it. They call the play in the huddle and can change it at the line if the defence looks wrong.",
    whyItMatters:
      "It's the most important position in the sport. Commentary spends more time on the quarterback than everyone else combined.",
    examples: ["The player standing behind the centre shouting before the ball is snapped."],
    relatedConcepts: ["sack", "interception", "wide-receiver"],
  },
  {
    conceptId: "running-back",
    title: "Running Back (RB)",
    emoji: "🏃",
    category: "Who's who",
    difficulty: DIFFICULTY.BASICS,
    shortExplanation: "Takes handoffs and runs. Also blocks and catches short passes.",
    beginnerExplanation:
      "The running back lines up near the quarterback and usually takes a handoff to run forward through the defence. Modern running backs also catch passes and block defenders who are chasing the quarterback.",
    whyItMatters:
      "Running plays are steady and safe: they gain a few yards and keep the clock moving.",
    examples: ["\"4 yard run\" in the play feed almost always means the running back."],
    relatedConcepts: ["quarterback", "fumble", "offensive-line"],
  },
  {
    conceptId: "wide-receiver",
    title: "Wide Receiver (WR)",
    emoji: "🙋",
    category: "Who's who",
    difficulty: DIFFICULTY.BASICS,
    shortExplanation: "Lines up wide, runs downfield, catches passes.",
    beginnerExplanation:
      "Wide receivers start near the sidelines and sprint into open space so the quarterback can throw to them. They're usually the fastest players on the field and are responsible for the long, dramatic plays.",
    whyItMatters:
      "Big passing plays are how teams cover ground quickly. Receivers are how that happens.",
    examples: ["A player sprinting 30 yards downfield with a defender chasing him."],
    relatedConcepts: ["quarterback", "incomplete-pass", "defensive-back"],
  },
  {
    conceptId: "tight-end",
    title: "Tight End (TE)",
    emoji: "🧱",
    category: "Who's who",
    difficulty: DIFFICULTY.MECHANICS,
    shortExplanation: "Half blocker, half receiver. Lines up beside the offensive line.",
    beginnerExplanation:
      "The tight end is a hybrid: big enough to block like a lineman, but able to catch passes like a receiver. Because the defence can't tell which job they're doing until the ball is snapped, they're genuinely awkward to defend.",
    whyItMatters:
      "Tight ends are often a quarterback's safety valve on third down.",
    examples: ["A large player lined up right next to the linemen who suddenly runs a route."],
    relatedConcepts: ["wide-receiver", "offensive-line", "third-down"],
  },
  {
    conceptId: "offensive-line",
    title: "Offensive Line (OL)",
    emoji: "🛡️",
    category: "Who's who",
    difficulty: DIFFICULTY.MECHANICS,
    shortExplanation: "The five big players up front whose job is to protect and clear space.",
    beginnerExplanation:
      "Five players line up shoulder to shoulder in front of the quarterback. One of them, the centre, starts every play by snapping the ball backwards. The rest block defenders, either protecting the quarterback or opening a gap for the runner.",
    whyItMatters:
      "They never touch the ball and are almost never mentioned, but no offence works without them.",
    examples: ["If the quarterback gets sacked, the offensive line usually got beaten."],
    relatedConcepts: ["sack", "running-back", "defensive-line"],
  },

  /* ------------------------------------------------ defence positions */
  {
    conceptId: "defensive-line",
    title: "Defensive Line (DL)",
    emoji: "⚔️",
    category: "Who's who",
    difficulty: DIFFICULTY.MECHANICS,
    shortExplanation: "The front-line defenders who attack the quarterback and stop runs.",
    beginnerExplanation:
      "The defensive line starts right across from the offensive line. On a passing play they try to fight through and reach the quarterback; on a running play they try to plug the gaps before the runner gets through.",
    whyItMatters:
      "Pressure from the defensive line is the cheapest way to ruin a passing play: no extra defenders required.",
    examples: ["The players who cause most sacks."],
    relatedConcepts: ["sack", "offensive-line", "linebacker"],
  },
  {
    conceptId: "linebacker",
    title: "Linebacker (LB)",
    emoji: "🧭",
    category: "Who's who",
    difficulty: DIFFICULTY.MECHANICS,
    shortExplanation: "Defenders a few yards back who do a bit of everything.",
    beginnerExplanation:
      "Linebackers stand behind the defensive line. Depending on the play they'll tackle runners, cover receivers, or rush the quarterback. They're usually the ones calling out adjustments for the defence.",
    whyItMatters:
      "They're the defence's decision-makers, and often lead the team in tackles.",
    examples: ["A defender who chases the running back down after a short gain."],
    relatedConcepts: ["blitz", "defensive-line", "defensive-back"],
  },
  {
    conceptId: "defensive-back",
    title: "Defensive Back (DB)",
    emoji: "👀",
    category: "Who's who",
    difficulty: DIFFICULTY.MECHANICS,
    shortExplanation: "Cornerbacks and safeties: the players who cover receivers.",
    beginnerExplanation:
      "Defensive backs line up away from the crowd, matched against wide receivers. Cornerbacks usually stick to a receiver; safeties play deeper and act as the last line of defence. Interceptions almost always come from this group.",
    whyItMatters:
      "They're the reason a wide-open receiver is rare, and the reason a deep pass is risky.",
    examples: ["The defender running stride for stride with a receiver down the sideline."],
    relatedConcepts: ["interception", "wide-receiver", "linebacker"],
  },

  /* ------------------------------------------------ strategy */
  {
    conceptId: "blitz",
    title: "Blitz",
    emoji: "🔥",
    category: "Strategy",
    difficulty: DIFFICULTY.STRATEGY,
    shortExplanation: "Sending extra defenders after the quarterback. High risk, high reward.",
    beginnerExplanation:
      "Normally four defenders rush the quarterback. A blitz sends more (a linebacker or defensive back joins in) to get there faster. The trade-off is real: those extra rushers aren't covering receivers, so if the quarterback gets the pass away, somebody is open.",
    whyItMatters:
      "A blitz is a gamble. When it works it's a sack; when it fails it's often a very long gain.",
    examples: [
      "A linebacker suddenly sprints at the quarterback instead of dropping back.",
      "\"They blitzed and he beat it\": the quarterback threw before the pressure arrived.",
    ],
    relatedConcepts: ["sack", "quarterback", "linebacker"],
  },
]);

/** conceptId → concept, built once. */
export const CONCEPTS_BY_ID = Object.freeze(
  CONCEPTS.reduce((acc, concept) => {
    acc[concept.conceptId] = concept;
    return acc;
  }, {})
);

export const CONCEPT_IDS = Object.freeze(CONCEPTS.map((c) => c.conceptId));
